import * as React from 'react';
import { overlayAnimation, panelAnimation, panelAnimationStyle } from '@wayste/shadcn/lib/animations';
import { cn } from '@wayste/shadcn/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { Button } from './button';
import { Toolbar, type ToolbarProps, ToolbarTitle } from './toolbar';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(overlayAnimation, 'fixed inset-0 z-[350] bg-black/50', className)}
            {...props}
        />
    );
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
}) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    panelAnimation,
                    'border-border-default bg-surface-tertiary fixed left-[50%] top-[50%] z-[350] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg sm:max-w-lg',
                    className,
                )}
                style={panelAnimationStyle}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className="data-[state=open]:bg-surface-secondary data-[state=open]:text-text-secondary rounded-xs focus:outline-hidden focus:ring-border-focus focus:ring-offset-surface-primary absolute right-4 top-4 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
                    >
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-header" className={cn('flex flex-col gap-2 text-center sm:text-left', className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-footer" className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />;
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg font-semibold leading-none', className)} {...props} />;
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description data-slot="dialog-description" className={cn('text-text-secondary text-sm', className)} {...props} />
    );
}

interface DialogToolbarProps extends ToolbarProps {
    /**
     * Show a close button on the right. Set to false to manage the close
     * button manually inside the children.
     * @default true
     */
    showCloseButton?: boolean;
}

/**
 * A Toolbar-backed header for Dialog modals. Place a ToolbarTitle inside as
 * the title slot; any additional children (buttons, pills) render to the right.
 *
 * When using DialogToolbar, pass `showCloseButton={false}` on DialogContent to
 * avoid rendering two close buttons.
 *
 * @example
 * ```tsx
 * <DialogContent showCloseButton={false}>
 *   <DialogToolbar>
 *     <ToolbarTitle description="Fill out the form below.">Create order</ToolbarTitle>
 *     <Button size="sm" variant="outline">Cancel</Button>
 *     <Button size="sm">Save</Button>
 *   </DialogToolbar>
 *   ...
 * </DialogContent>
 * ```
 */
function DialogToolbar({ children, showCloseButton = true, size = 'default', ...props }: DialogToolbarProps) {
    return (
        <Toolbar size={size} {...props}>
            {children}
            {showCloseButton && (
                <DialogClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close">
                        <XIcon className="size-4" />
                        <span className="sr-only">Close</span>
                    </Button>
                </DialogClose>
            )}
        </Toolbar>
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogToolbar,
    DialogTrigger,
};
export type { DialogToolbarProps };

// Re-export ToolbarTitle so consumers can import from one place
export { ToolbarTitle };
